<template>
  <BasicTemplate>
    <v-card>
      <v-card-title class="d-flex align-center">
        <h3 class="d-flex align-center mb-0" data-testid="game-data-change-log-page-title">
          <v-icon class="mr-2">mdi-history</v-icon>
          Change Log
        </h3>
        <v-spacer />
        <v-btn
          class="mr-2"
          color="info"
          :loading="isLogLoadInProgress"
          data-testid="game-data-change-log-search"
          @click="search()"
        >
          <v-icon left>mdi-magnify</v-icon>
          Search
        </v-btn>
        <v-btn
          outlined
          data-testid="game-data-change-log-clear"
          @click="clearFilters()"
        >
          Clear Filters
        </v-btn>
      </v-card-title>

      <v-card-text>
        <div v-if="activeFiltersCount > 0" class="mb-3">
          <v-chip color="info" small>
            <v-icon small left>mdi-filter</v-icon>
            {{ activeFiltersCount }} filter{{ activeFiltersCount > 1 ? 's' : '' }} active
          </v-chip>
        </div>

        <v-card class="mb-4 change-log-filters__section" elevation="1">
          <v-card-title class="change-log-filters__section-title">Filters</v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  id="userName"
                  v-model="filters.userName"
                  label="User Name"
                  clearable
                  dense
                  hide-details
                  data-testid="change-log-user-name"
                  @keyup.enter="search"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <v-autocomplete
                  id="developer"
                  v-model="filters.developer"
                  :items="developerOptions"
                  label="Developer"
                  clearable
                  dense
                  hide-details
                  hide-no-data
                  data-testid="change-log-developer"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <v-select
                  id="method"
                  v-model="filters.method"
                  :items="methodOptions"
                  label="Method"
                  clearable
                  dense
                  hide-details
                  data-testid="change-log-method"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  id="url"
                  v-model="filters.url"
                  label="URL"
                  clearable
                  dense
                  hide-details
                  data-testid="change-log-url"
                  @keyup.enter="search"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="change-log-date-field" data-testid="change-log-date-from">
                  <v-text-field
                    id="dateFrom"
                    v-model="filters.dateFrom"
                    label="From"
                    type="datetime-local"
                    clearable
                    dense
                    hide-details
                    class="change-log-date-field__input"
                  />
                  <div class="change-log-date-field__presets">
                    <v-btn x-small outlined title="Last Hour" @click="setLastHour">1h</v-btn>
                    <v-btn x-small outlined title="Last 24 Hours" @click="setLast24Hours">24h</v-btn>
                    <v-btn x-small outlined title="Last Week" @click="setLastWeek">7d</v-btn>
                    <v-btn x-small outlined title="Clear From date" @click="filters.dateFrom = ''">✕</v-btn>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="change-log-date-field" data-testid="change-log-date-to">
                  <v-text-field
                    id="dateTo"
                    v-model="filters.dateTo"
                    label="To"
                    type="datetime-local"
                    clearable
                    dense
                    hide-details
                    class="change-log-date-field__input"
                  />
                  <div class="change-log-date-field__presets">
                    <v-btn x-small outlined title="Clear To date" @click="filters.dateTo = ''">✕</v-btn>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <v-combobox
                  id="table"
                  v-model="filters.table"
                  :items="tableOptions"
                  label="Table"
                  clearable
                  dense
                  hide-details
                  data-testid="change-log-table"
                  @keyup.enter="search"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  id="entityId"
                  v-model="filters.entityId"
                  label="Entity ID"
                  clearable
                  dense
                  hide-details
                  data-testid="change-log-entity-id"
                  @keyup.enter="search"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <v-select
                  id="action"
                  v-model="filters.action"
                  :items="actionOptions"
                  label="Action"
                  clearable
                  dense
                  hide-details
                  data-testid="change-log-action"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <div v-if="!isLogLoadInProgress && logs.length > 0">
          <div class="log-table-container">
            <table class="paleBlueRows log-table">
              <thead>
                <tr>
                  <th scope="col">Method</th>
                  <th scope="col">URL</th>
                  <th scope="col">Entity</th>
                  <th scope="col">Name</th>
                  <th scope="col">Slug</th>
                  <th scope="col">Developer</th>
                  <th scope="col">User Name</th>
                  <th scope="col">Items</th>
                  <th scope="col">Timestamp</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(log, index) in logs"
                  :key="log.id || index"
                  class="log-row-clickable"
                  @click="showLogDetails(log)"
                >
                  <td>{{ log.method || '-' }}</td>
                  <td>{{ log.url || '-' }}</td>
                  <td>{{ log.entity || '-' }}</td>
                  <td>{{ log.entityName || '-' }}</td>
                  <td>{{ log.entitySlug || '-' }}</td>
                  <td>{{ log.developer || '-' }}</td>
                  <td>{{ log.userName || '-' }}</td>
                  <td>{{ log.itemsCount }}</td>
                  <td>{{ formatDate(log.timestamp) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="pagination-controls">
            <div class="pagination-info">
              Showing {{ (currentPage - 1) * pageSize + 1 }} - {{ (currentPage - 1) * pageSize + logs.length }} logs (Page {{ currentPage }})
            </div>
            <div class="pagination-buttons">
              <v-btn
                small
                color="primary"
                :disabled="currentPage === 1"
                @click="goToPage(currentPage - 1)"
              >
                Previous
              </v-btn>
              <span class="page-info">Page {{ currentPage }}</span>
              <v-btn
                small
                color="primary"
                :disabled="!hasMorePages"
                @click="goToPage(currentPage + 1)"
              >
                Next
              </v-btn>
            </div>
            <div class="page-size-selector">
              <label for="pageSize">Items per page:</label>
              <select id="pageSize" v-model.number="pageSize" @change="onPageSizeChange">
                <option :value="10">10</option>
                <option :value="25">25</option>
                <option :value="50">50</option>
                <option :value="100">100</option>
              </select>
            </div>
          </div>
        </div>

        <div v-else-if="!isLogLoadInProgress && logs.length === 0 && hasSearched" class="py-8 text-center">
          <p class="no-results mb-0">No logs found matching your search criteria.</p>
        </div>

        <div v-else-if="isLogLoadInProgress" class="d-flex flex-column align-center py-8">
          <v-progress-circular indeterminate color="primary" class="mb-3" />
          <p class="mb-0 text--secondary">Loading logs...</p>
        </div>
      </v-card-text>

      <v-dialog v-model="detailsDialog" max-width="1200px" scrollable persistent>
        <v-card class="admin-dialog change-log-details">
          <v-card-title class="d-flex align-center admin-dialog__title">
            <v-icon class="mr-2">mdi-file-document-outline</v-icon>
            <span class="font-weight-bold">Change Log Details</span>
            <v-spacer />
            <v-btn icon small @click="closeDetailsDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text class="admin-dialog__body" v-if="logDetails">
            <div v-if="isDetailsLoading" class="d-flex justify-center py-8">
              <v-progress-circular indeterminate color="primary" />
            </div>
            <div v-else>
              <v-card class="mb-4 change-log-details__request" elevation="1">
                <v-card-title class="d-flex align-center change-log-details__section-title">
                  <v-icon class="mr-2">mdi-information-outline</v-icon>
                  <span class="font-weight-bold">Request Information</span>
                </v-card-title>
                <v-card-text class="change-log-details__section-body">
                  <v-row dense>
                    <v-col cols="12" sm="6" md="3">
                      <v-text-field
                        :value="logDetails.id"
                        label="ID"
                        dense
                        hide-details
                        readonly
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-text-field
                        :value="logDetails.requestId"
                        label="Request ID"
                        dense
                        hide-details
                        readonly
                        class="change-log-details__mono-field"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-text-field
                        :value="logDetails.developer || '—'"
                        label="Developer"
                        dense
                        hide-details
                        readonly
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-text-field
                        :value="logDetails.userSub || '—'"
                        label="User Sub"
                        dense
                        hide-details
                        readonly
                        class="change-log-details__mono-field"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-text-field
                        :value="logDetails.userName"
                        label="User Name"
                        dense
                        hide-details
                        readonly
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="6">
                      <div class="change-log-details__roles">
                        <div class="change-log-details__roles-label">User Roles</div>
                        <div class="change-log-details__roles-chips">
                          <template v-if="logDetails.userRoles">
                            <v-chip
                              v-for="(role, idx) in logDetails.userRoles.split(',')"
                              :key="idx"
                              x-small
                              color="primary"
                              class="mr-1 mb-1"
                            >
                              {{ role.trim() }}
                            </v-chip>
                          </template>
                          <span v-else class="grey--text">—</span>
                        </div>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-text-field
                        :value="formatDate(logDetails.timestamp)"
                        label="Timestamp"
                        dense
                        hide-details
                        readonly
                      />
                    </v-col>
                    <v-col cols="12" sm="4" md="3">
                      <v-text-field
                        :value="logDetails.method"
                        label="Method"
                        dense
                        hide-details
                        readonly
                      />
                    </v-col>
                    <v-col cols="12" sm="8" md="6">
                      <v-text-field
                        :value="logDetails.url"
                        label="URL"
                        dense
                        hide-details
                        readonly
                        class="change-log-details__mono-field"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-text-field
                        :value="logDetails.query || '—'"
                        label="Query"
                        dense
                        hide-details
                        readonly
                        class="change-log-details__mono-field"
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <v-card class="mb-0 change-log-details__items" elevation="1">
                <v-card-title class="d-flex align-center change-log-details__section-title">
                  <v-icon class="mr-2">mdi-format-list-bulleted</v-icon>
                  <span class="font-weight-bold">Change Log Items</span>
                  <v-spacer />
                  <v-chip
                    v-if="logDetails.changeLogItems && logDetails.changeLogItems.length"
                    small
                    color="info"
                  >
                    {{ logDetails.changeLogItems.length }}
                  </v-chip>
                </v-card-title>
                <v-card-text class="pt-0">
                  <div v-if="logDetails.changeLogItems && logDetails.changeLogItems.length > 0">
                    <v-expansion-panels class="change-log-details__panels" accordion>
                      <v-expansion-panel
                        v-for="(item, index) in logDetails.changeLogItems"
                        :key="item.id || index"
                        class="change-log-details__panel"
                      >
                        <v-expansion-panel-header class="change-log-details__panel-header">
                          <div class="d-flex align-center flex-wrap">
                            <v-chip
                              x-small
                              label
                              color="primary"
                              outlined
                              class="mr-2"
                            >
                              {{ item.action }}
                            </v-chip>
                            <span class="change-log-details__panel-title">
                              {{ item.table }}
                              <span class="grey--text">(ID: {{ item.entityId }})</span>
                            </span>
                          </div>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                          <div v-if="item.entity || item.databaseEntity" class="diff-container">
                            <div class="diff-header">
                              <div class="diff-header__label">Changed fields</div>
                              <div class="diff-header__chips">
                                <v-chip
                                  v-for="(field, idx) in getChangedFields(item.entity, item.databaseEntity)"
                                  :key="idx"
                                  x-small
                                  color="warning"
                                  class="mr-1 mb-1"
                                >
                                  {{ field }}
                                </v-chip>
                              </div>
                            </div>
                          </div>
                          <v-row dense>
                            <v-col cols="12" md="6">
                              <div class="change-log-details__json-label change-log-details__json-label--new">
                                Entity (New)
                              </div>
                              <div
                                v-if="item.entity"
                                class="json-preview json-preview-with-diff"
                              >
                                <pre v-html="getHighlightedJson(item.entity, item.databaseEntity, true)"></pre>
                              </div>
                              <span v-else class="grey--text">—</span>
                            </v-col>
                            <v-col cols="12" md="6">
                              <div class="change-log-details__json-label change-log-details__json-label--old">
                                Database Entity (Old)
                              </div>
                              <div
                                v-if="item.databaseEntity"
                                class="json-preview json-preview-with-diff"
                              >
                                <pre v-html="getHighlightedJson(item.databaseEntity, item.entity, false)"></pre>
                              </div>
                              <span v-else class="grey--text">—</span>
                            </v-col>
                          </v-row>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </div>
                  <div v-else class="text-center pa-6">
                    <v-icon size="48" color="grey lighten-1">mdi-playlist-remove</v-icon>
                    <p class="mt-2 mb-0 grey--text">No change log items</p>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </v-card-text>

          <v-card-actions class="admin-dialog__actions">
            <v-spacer />
            <v-btn outlined large @click="closeDetailsDialog">
              Close
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </BasicTemplate>
</template>

<script>
  import BasicTemplate from '../../templates/basic.vue'
  import persistentDialogEsc from '@/mixins/persistentDialogEsc'

  export default {
    components: { BasicTemplate },
    name: 'GameDataChangeLog',
    mixins: [
      persistentDialogEsc({
        detailsDialog: 'closeDetailsDialog',
      }),
    ],
    props: [],
    data () {
      return {
        logs: [],
        developers: [],
        filters: {
          userName: '',
          url: '',
          developer: '',
          method: '',
          dateFrom: '',
          dateTo: '',
          action: '',
          table: '',
          entityId: ''
        },
        currentPage: 1,
        pageSize: 10,
        totalCount: 0,
        totalPages: 0,
        hasMorePages: false, // Флаг что есть еще страницы
        isLogLoadInProgress: false,
        hasSearched: false,
        detailsDialog: false,
        logDetails: null,
        isDetailsLoading: false,
        gamesCache: {}, // Cache for games by ID: { [id]: { slug, name } }
        feedsCache: {}, // Cache for feeds by ID: { [id]: { name } }
        allGamesLoaded: false,
        allFeedsLoaded: false,
        tableOptions: [
          "analytics_config",
          "analytics_config_domain_sampling",
          "badge",
          "badge_feed_game_meta",
          "badge_game_meta",
          "category",
          "category_feed_game_meta",
          "category_game_meta",
          // "change_log",
          // "change_log_item",
          "developer",
          "domain",
          "feed",
          "feed_game",
          "feed_game_meta",
          "feed_game_preroll_settings",
          "feed_game_rollout_settings",
          "feed_game_support_info",
          "feed_game_thumb",
          "game",
          "game_analytics_config",
          "game_meta",
          "game_note",
          "game_preroll_settings",
          "game_support_info",
          "game_thumb",
          "game_version",
          "game_version_old",
          "meta",
          "meta_badge",
          "meta_category",
          "meta_tag",
          "meta_template",
          "meta_template__game_suggestion",
          "meta_template_game",
          "meta_thumb",
          "migrations",
          "tag",
          "tag_feed_game_meta",
          "tag_game_meta"
        ]
      }
    },
    created: async function () {
      const { table, entityId } = this.$route.query
      if (table) this.filters.table = String(table)
      if (entityId) this.filters.entityId = String(entityId)
      await this.loadDevelopers()
      await this.preloadAllGamesAndFeeds()
      this.fetchLogs()
    },
    computed: {
      methodOptions() {
        return ['GET', 'POST', 'PATCH', 'DELETE']
      },
      actionOptions() {
        return [
          { text: 'Insert', value: 'insert' },
          { text: 'Update', value: 'update' },
          { text: 'Delete', value: 'delete' },
        ]
      },
      developerOptions() {
        return (this.developers || [])
          .filter((dev) => dev && dev.name)
          .map((dev) => dev.name)
      },
      activeFiltersCount() {
        let count = 0
        if (this.filters.userName) count++
        if (this.filters.url) count++
        if (this.filters.developer) count++
        if (this.filters.method) count++
        if (this.filters.dateFrom) count++
        if (this.filters.dateTo) count++
        if (this.filters.action) count++
        if (this.filters.table) count++
        if (this.filters.entityId) count++
        return count
      }
    },
    methods: {
      closeDetailsDialog() {
        this.detailsDialog = false
      },
      async preloadAllGamesAndFeeds() {
        try {
          // Load all games and feeds in parallel
          const [games, feeds] = await Promise.all([
            this.$gameApi.get('/games'),
            this.$gameApi.get('/feeds')
          ])

          // Populate games cache
          if (Array.isArray(games)) {
            games.forEach(game => {
              if (game.id) {
                this.gamesCache[game.id] = {
                  slug: game.slug || null,
                  name: game.name || null
                }
              }
            })
            console.log(`Preloaded ${games.length} games into cache`)
          }

          // Populate feeds cache
          if (Array.isArray(feeds)) {
            feeds.forEach(feed => {
              if (feed.id) {
                this.feedsCache[feed.id] = {
                  name: feed.name || null
                }
              }
            })
            console.log(`Preloaded ${feeds.length} feeds into cache`)
          }

          this.allGamesLoaded = true
          this.allFeedsLoaded = true
        } catch (error) {
          console.error('Error preloading games and feeds:', error)
          // Continue anyway - we'll fall back to individual requests if needed
        }
      },
      async loadDevelopers() {
        try {
          this.developers = await this.$gameApi.get('/users')
        } catch (error) {
          console.error('Error loading developers:', error)
          this.developers = []
        }
      },
      search() {
        // Reset to first page when searching
        this.currentPage = 1
        this.fetchLogs()
      },
      async fetchLogs() {
        this.isLogLoadInProgress = true
        this.hasSearched = true

        try {
          const params = this.buildChangeLogParams()
          const queryString = params.toString()
          const url = queryString ? `/ChangeLog?${queryString}` : '/ChangeLog'
          const response = await this.$gameApi.get(url)
          const logsArray = this.extractLogsArray(response)

          // If we got a full page, there might be more
          this.hasMorePages = logsArray.length >= this.pageSize
          this.logs = logsArray.map(log => this.toLogRow(log))
          this.totalCount = logsArray.length
        } catch (error) {
          console.error('Error fetching logs:', error)
          this.logs = []
          this.totalCount = 0
          this.hasMorePages = false
        } finally {
          this.isLogLoadInProgress = false
        }
      },
      buildChangeLogParams() {
        const params = new URLSearchParams()
        const skip = (this.currentPage - 1) * this.pageSize
        params.append('take', String(this.pageSize))
        params.append('skip', String(skip))
        this.appendFilterParams(params)
        this.appendDateParams(params)
        return params
      },
      appendFilterParams(params) {
        const keys = ['userName', 'url', 'developer', 'method', 'action', 'table', 'entityId']
        keys.forEach(key => {
          const value = this.filters[key]
          if (value) params.append(key, value)
        })
      },
      appendDateParams(params) {
        if (this.filters.dateFrom) {
          params.append('dateFrom', new Date(this.filters.dateFrom).toISOString())
        }
        if (this.filters.dateTo) {
          params.append('dateTo', this.toEndOfMinuteIso(this.filters.dateTo))
        }
      },
      toEndOfMinuteIso(value) {
        const localDate = new Date(value)
        const dateToEnd = new Date(
          localDate.getFullYear(),
          localDate.getMonth(),
          localDate.getDate(),
          localDate.getHours(),
          localDate.getMinutes(),
          59,
          999
        )
        return dateToEnd.toISOString()
      },
      extractLogsArray(response) {
        if (Array.isArray(response)) return response
        if (response == null || typeof response !== 'object') return []
        if (Array.isArray(response.data)) return response.data
        if (Array.isArray(response.items)) return response.items
        const arrayKey = Object.keys(response).find(key => Array.isArray(response[key]))
        if (arrayKey) return response[arrayKey]
        return []
      },
      toLogRow(log) {
        const entity = this.extractEntityFromUrl(log.url)
        const labels = this.resolveEntityLabels(log, entity)
        let patch = null
        if (Object.hasOwn(log, 'patch')) {
          patch = log.patch
        }
        return {
          id: log.id,
          changeLogId: log.id,
          requestId: log.requestId,
          userName: log.userName,
          method: log.method,
          url: log.url,
          entity,
          entityName: labels.entityName,
          entitySlug: labels.entitySlug,
          developer: log.developer,
          userRoles: log.userRoles,
          timestamp: log.timestamp,
          patch,
          itemsCount: this.changeLogItemsCount(log),
          action: null,
          table: null,
          name: null,
          slug: null
        }
      },
      changeLogItemsCount(log) {
        if (Array.isArray(log.changeLogItems)) return log.changeLogItems.length
        const count = Number(log.itemsCount)
        if (Number.isFinite(count)) return count
        return 0
      },
      resolveEntityLabels(log, entity) {
        const empty = { entityName: null, entitySlug: null }
        if (log.method === 'DELETE') return empty
        const entityId = this.extractIdFromUrl(log.url, entity)
        if (entity === 'game' && entityId) return this.gameLabels(entityId)
        if (entity === 'feed' && entityId) {
          return { entityName: this.getFeedNameSync(entityId), entitySlug: null }
        }
        return empty
      },
      gameLabels(entityId) {
        const gameData = this.getGameDataSync(entityId)
        if (gameData) {
          return {
            entityName: gameData.name || null,
            entitySlug: gameData.slug || null
          }
        }
        return { entityName: null, entitySlug: null }
      },
      goToPage(page) {
        if (page >= 1) {
          // Allow going to next page if hasMorePages
          // Allow going to previous page if page > 1
          if (page > this.currentPage && !this.hasMorePages) {
            return // Can't go forward if no more pages
          }
          this.currentPage = page
          this.fetchLogs()
        }
      },
      onPageSizeChange() {
        this.currentPage = 1
        this.fetchLogs()
      },
      clearFilters() {
        this.filters = {
          userName: '',
          url: '',
          developer: '',
          method: '',
          dateFrom: '',
          dateTo: '',
          action: '',
          table: '',
          entityId: ''
        }
        this.currentPage = 1
        this.fetchLogs()
      },
      setLastHour() {
        const now = new Date()
        const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000)
        this.filters.dateFrom = this.formatDateTimeLocal(oneHourAgo)
        this.filters.dateTo = this.formatDateTimeLocal(now)
      },
      setLast24Hours() {
        const now = new Date()
        const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000)
        this.filters.dateFrom = this.formatDateTimeLocal(oneDayAgo)
        this.filters.dateTo = this.formatDateTimeLocal(now)
      },
      setLastWeek() {
        const now = new Date()
        const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
        this.filters.dateFrom = this.formatDateTimeLocal(oneWeekAgo)
        this.filters.dateTo = this.formatDateTimeLocal(now)
      },
      formatDateTimeLocal(date) {
        // Format date to datetime-local input format: YYYY-MM-DDTHH:mm
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        return `${year}-${month}-${day}T${hours}:${minutes}`
      },
      async showLogDetails(log) {
        // Use changeLogId if available, otherwise use id
        const logId = log.changeLogId || log.id
        if (!logId) {
          console.error('No log ID available')
          return
        }

        this.detailsDialog = true
        this.isDetailsLoading = true
        this.logDetails = null

        try {
          this.logDetails = await this.$gameApi.get(`/ChangeLog/${logId}`)
        } catch (error) {
          console.error('Error fetching log details:', error)
          this.$swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Failed to load log details'
          })
          this.detailsDialog = false
        } finally {
          this.isDetailsLoading = false
        }
      },
      formatDate: function (dateString) {
        if (!dateString) return '-'
        try {
          const date = new Date(dateString)
          return date.toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
          })
        } catch (e) {
          return dateString
        }
      },
      findAllDifferentKeys: function (obj1, obj2, path = '', result = new Set()) {
        if (obj1 === undefined && obj2 === undefined) return result

        // Handle null
        if (obj1 === null || obj2 === null) {
          if (obj1 !== obj2) {
            result.add(path || 'root')
          }
          return result
        }

        // Handle arrays
        if (Array.isArray(obj1) || Array.isArray(obj2)) {
          if (JSON.stringify(obj1) !== JSON.stringify(obj2)) {
            result.add(path || 'root')
          }
          return result
        }

        // Handle primitives
        if (typeof obj1 !== 'object' || typeof obj2 !== 'object') {
          if (obj1 !== obj2) {
            result.add(path || 'root')
          }
          return result
        }

        // Get all keys from both objects
        const keys1 = Object.keys(obj1 || {})
        const keys2 = Object.keys(obj2 || {})
        const allKeys = new Set([...keys1, ...keys2])

        for (const key of allKeys) {
          const currentPath = path ? `${path}.${key}` : key
          const val1 = obj1[key]
          const val2 = obj2[key]

          // Key exists in only one object
          if (!(key in obj1) || !(key in obj2)) {
            result.add(currentPath)
          } else {
            // Both have the key, compare values
            const val1Type = typeof val1
            const val2Type = typeof val2

            // If both are objects (but not arrays), recurse
            if (val1Type === 'object' && val2Type === 'object' && val1 !== null && val2 !== null && !Array.isArray(val1) && !Array.isArray(val2)) {
              this.findAllDifferentKeys(val1, val2, currentPath, result)
            } else {
              // Compare primitive values or arrays
              if (JSON.stringify(val1) !== JSON.stringify(val2)) {
                result.add(currentPath)
              }
            }
          }
        }

        return result
      },
      escapeHtml: function (text) {
        const div = document.createElement('div')
        div.textContent = text
        return div.innerHTML
      },
      getChangedFields: function (entity, databaseEntity) {
        if (!entity || !databaseEntity) {
          return []
        }

        const changedFields = []
        const allKeys = new Set([...Object.keys(entity || {}), ...Object.keys(databaseEntity || {})])

        for (const key of allKeys) {
          const val1 = entity[key]
          const val2 = databaseEntity[key]

          if (!(key in entity) || !(key in databaseEntity)) {
            changedFields.push(key)
          } else if (JSON.stringify(val1) !== JSON.stringify(val2)) {
            changedFields.push(key)
          }
        }

        return changedFields.sort()
      },
      getHighlightedJson: function (obj, compareObj, isNew) {
        try {
          if (!obj) {
            return this.escapeHtml('')
          }

          const jsonStr = JSON.stringify(obj, null, 2)
          const differentKeys = this.findAllDifferentKeys(obj, compareObj || {})

          // Build sets of all keys that appear in different paths (at any level)
          const allDifferentKeys = new Set()
          for (const path of differentKeys) {
            const parts = path.split('.')
            // Add all parts of the path
            parts.forEach(part => allDifferentKeys.add(part))
          }

          // Split JSON string by lines
          const lines = jsonStr.split('\n')
          const highlightedLines = []
          let inHighlightedBlock = false
          let highlightIndent = -1

          for (let i = 0; i < lines.length; i++) {
            const line = lines[i]
            const trimmed = line.trim()
            const indent = line.match(/^(\s*)/)[1].length

            // Extract key from line
            const keyMatch = trimmed.match(/^"([^"]+)":\s*/)
            if (keyMatch) {
              const key = keyMatch[1]
              // Simple check: if key appears in any different path
              const isDifferent = allDifferentKeys.has(key)

              if (isDifferent) {
                inHighlightedBlock = true
                highlightIndent = indent
                highlightedLines.push(`<span class="json-diff-highlight">${this.escapeHtml(line)}</span>`)
              } else {
                if (inHighlightedBlock && indent > highlightIndent) {
                  highlightedLines.push(`<span class="json-diff-highlight">${this.escapeHtml(line)}</span>`)
                } else {
                  inHighlightedBlock = false
                  highlightIndent = -1
                  highlightedLines.push(this.escapeHtml(line))
                }
              }
            } else {
              if (inHighlightedBlock) {
                if (trimmed === '}' || trimmed === ']') {
                  if (indent === highlightIndent) {
                    highlightedLines.push(`<span class="json-diff-highlight">${this.escapeHtml(line)}</span>`)
                    inHighlightedBlock = false
                    highlightIndent = -1
                  } else if (indent > highlightIndent) {
                    highlightedLines.push(`<span class="json-diff-highlight">${this.escapeHtml(line)}</span>`)
                  } else {
                    inHighlightedBlock = false
                    highlightIndent = -1
                    highlightedLines.push(this.escapeHtml(line))
                  }
                } else if (indent > highlightIndent) {
                  highlightedLines.push(`<span class="json-diff-highlight">${this.escapeHtml(line)}</span>`)
                } else {
                  inHighlightedBlock = false
                  highlightIndent = -1
                  highlightedLines.push(this.escapeHtml(line))
                }
              } else {
                highlightedLines.push(this.escapeHtml(line))
              }
            }
          }

          return highlightedLines.join('\n')
        } catch (e) {
          console.error('Error highlighting JSON:', e)
          return this.escapeHtml(JSON.stringify(obj, null, 2))
        }
      },
      extractEntityFromUrl: function (url) {
        if (!url) return null
        // Remove leading slash if present and query parameters
        let trimmed = url.startsWith('/') ? url.substring(1) : url
        // Remove query parameters if present
        const queryIndex = trimmed.indexOf('?')
        if (queryIndex !== -1) {
          trimmed = trimmed.substring(0, queryIndex)
        }
        const parts = trimmed.split('/')
        const firstPart = parts.length > 0 ? parts[0] : null
        // Normalize "games" to "game" for consistency
        if (firstPart === 'games') {
          return 'game'
        }
        // Normalize "feeds" to "feed" for consistency
        if (firstPart === 'feeds') {
          return 'feed'
        }
        return firstPart
      },
      extractIdFromUrl: function (url, entity) {
        if (!url || !entity) return null
        // Remove leading slash if present and query parameters
        let trimmed = url.startsWith('/') ? url.substring(1) : url
        // Remove query parameters if present
        const queryIndex = trimmed.indexOf('?')
        if (queryIndex !== -1) {
          trimmed = trimmed.substring(0, queryIndex)
        }
        const parts = trimmed.split('/')
        // ID should be after the entity (second segment)
        // Handle both "game"/"games" and "feed"/"feeds" for entities
        if (parts.length > 1) {
          const firstPart = parts[0]
          if (entity === 'game' && (firstPart === 'game' || firstPart === 'games')) {
            // Extract ID and remove any trailing path segments
            const id = parts[1]
            return id ? id.split('?')[0] : null
          } else if ((entity === 'feed' || entity === 'feeds') && (firstPart === 'feed' || firstPart === 'feeds')) {
            const id = parts[1]
            return id ? id.split('?')[0] : null
          }
        }
        return null
      },
      getGameDataSync(gameId) {
        if (!gameId) return null

        // Return from cache only
        if (this.gamesCache[gameId]) {
          return this.gamesCache[gameId]
        }

        return null
      },
      getFeedNameSync(feedId) {
        if (!feedId) return null

        // Return from cache only
        if (this.feedsCache[feedId]) {
          return this.feedsCache[feedId].name
        }

        return null
      }
    }
  }
</script>

<style scoped lang="scss" src="./GameDataChangeLog.scss">

</style>
